import { createContext, useContext, useMemo, useState } from "react";

const FilterContext = createContext(null);

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

const fyRange = () => {
  const now = new Date();
  const y = now.getMonth() >= 2 ? now.getFullYear() : now.getFullYear() - 1;
  return { from: iso(new Date(y, 2, 1)), to: iso(new Date(y + 1, 2, 0)) };
};

export const PRESETS = [
  { key: "today", label: "Today", range: () => { const t = iso(new Date()); return { from: t, to: t }; } },
  { key: "fy", label: "Financial YTD", range: fyRange },
  { key: "month", label: "This month", range: () => { const n = new Date(); return { from: iso(new Date(n.getFullYear(), n.getMonth(), 1)), to: iso(new Date(n.getFullYear(), n.getMonth() + 1, 0)) }; } },
  { key: "q", label: "Last 90 days", range: () => { const n = new Date(); return { from: iso(new Date(n.getFullYear(), n.getMonth(), n.getDate() - 89)), to: iso(n) }; } },
  { key: "all", label: "All data", range: () => ({ from: "2020-01-01", to: "2030-12-31" }) },
];

const DEFAULT = {
  ...fyRange(),
  customerIds: [],
  machineIds: [],
  gradeIds: [],
  materialOwnershipIds: [],
  thicknessIds: [],
  programmerIds: [],
  statuses: ["active", "corrected"],
  search: "",
  preset: "fy",
};

export function FilterProvider({ children }) {
  const [filter, setFilter] = useState(DEFAULT);

  const update = (patch) => setFilter((f) => ({ ...f, ...patch }));
  const reset = () => setFilter({ ...DEFAULT, ...fyRange() });

  const activeCount = useMemo(() => {
    return (
      filter.customerIds.length +
      filter.machineIds.length +
      filter.gradeIds.length +
      filter.materialOwnershipIds.length +
      filter.thicknessIds.length +
      filter.programmerIds.length +
      (filter.statuses.length !== 2 || !filter.statuses.includes("active") ? 1 : 0)
    );
  }, [filter]);

  const query = useMemo(() => {
    const p = new URLSearchParams();
    p.set("from", filter.from);
    p.set("to", filter.to);
    if (filter.customerIds.length) p.set("customerIds", filter.customerIds.join(","));
    if (filter.machineIds.length) p.set("machineIds", filter.machineIds.join(","));
    if (filter.gradeIds.length) p.set("gradeIds", filter.gradeIds.join(","));
    if (filter.materialOwnershipIds.length) p.set("materialOwnershipIds", filter.materialOwnershipIds.join(","));
    if (filter.thicknessIds.length) p.set("thicknessIds", filter.thicknessIds.join(","));
    if (filter.programmerIds.length) p.set("programmerIds", filter.programmerIds.join(","));
    if (filter.statuses.length) p.set("statuses", filter.statuses.join(","));
    if (filter.search) p.set("search", filter.search);
    return p.toString();
  }, [filter]);

  return (
    <FilterContext.Provider value={{ filter, update, reset, activeCount, query }}>
      {children}
    </FilterContext.Provider>
  );
}

export const useFilter = () => useContext(FilterContext);
